import { User } from '../auth/auth.state'
import { EmployeeSalary } from '../employee-salary/employee-salary.state'

export interface TdsCalculationResult {
  tdsForCurrentMonth: number
  finalNetSalary: number
  esiForCurrentMonth?: number
}

export interface TdsCalculationInput {
  employeeId: string
  payPeriodEnd: string
  currentMonthGross: number
  lopDays: number
  bonus: number
  expenses: number
  adjustments: number
  rounding: number
  tdsAfterCess?: number
  esi?: number
}

export interface EmployeePreviewInput {
  employeeId: string
  currentMonthGross: number
}

export interface TdsCalculationBatchInput {
  payPeriodEnd: string
  employees: EmployeePreviewInput[]
}

export interface PayrollCalculationContext {
  currentSalary: EmployeeSalary
  previousSalary: EmployeeSalary | null
  payPeriodStart: string // YYYY-MM-DD
  payPeriodEnd: string // YYYY-MM-DD
  lopDays: number
  lopDaysForArrearsPeriod: number
  bonus: number
  expenses: number
  adjustments: number
  rounding: number
  tdsAfterCess?: number
  netSalary?: number
  salaryHoldFrom?: string | null | Date
  salaryHoldTo?: string | null | Date
  esi?: number
}

export interface PayrollCalculationResult {
  grossSalary: number
  netSalary: number
  tdsAfterCess: number
  arrearsAmountDisplay: number
  calculationBreakdown: PayrollCalculationBreakdown | null
}

export interface SalaryComponent {
  name: string
  amount: number
}

export interface PayrollCalculationBreakdown {
  earnings: SalaryComponent[]
  deductions: SalaryComponent[]
  // More details here if needed, like prorated days for each salary, etc.
  // e.g., prorationDetails: [{ salaryId: '...', days: 15, gross: 30000 }, { salaryId: '...', days: 15, gross: 32000 }]
}

export interface Payroll {
  id: string | null
  employeeId: string
  belongsTo: string
  salaryId: string
  grossSalary: number
  netSalary: number
  tdsAfterCess: number
  lopDays: number
  bonus: number
  expenses: number
  adjustments: number
  rounding: number
  calculationBreakdown: PayrollCalculationBreakdown | null
  status: PayrollStatus
  batchId: string | null
  esi?: number
}

export enum PayrollStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  IN_PROCESS = 'in-process',
  PROCESSED = 'processed',
  PAID = 'paid',
  FAILED = 'failed'
}

export interface IncomingPayroll extends Payroll {
  salary: EmployeeSalary
  employee: User
  bankName: string
  bankAccountNumber: string
  pan: string
  businessName: string
  businessAddress: string
  businessLogo: string
  esiNumber?: string
}

export interface BasePayrollBatch {
  id: string | null
  belongsTo: string
  payPeriodStart: string
  payPeriodEnd: string
  netAmount: number | null
  investmentsLockedAt: string | null
  totalTdsAfterCess: number
  totalAmount: number | null
  employeeCount: number | null
  status: PayrollStatus
  processedBy: string | null
  paidBy: string | null
  // esi: number | null
}

export interface IncomingRunningPayrollBatch extends BasePayrollBatch {
  payrolls: IncomingPayroll[]
  updatedAt: string | null
  processedByUser: User | null
  processedOn: string | null
  paidByUser: User | null
  dateOfPayment: string | null
}

export interface RunningPayrollBatch extends BasePayrollBatch {
  payrolls: Payroll[] | null
}

//Used for manage payroll batches listing
export interface IncomingPayrollBatch {
  id: string
  belongsTo: string
  payPeriodStart: string
  payPeriodEnd: string
  netAmount: number
  investmentsLockedAt: string | null
  totalTdsAfterCess: number
  totalAmount: number
  employeeCount: number
  status: PayrollStatus
  paidBy: string | null
  paidByUser: User | null
  dateOfPayment: string | null
  processedBy: string | null
  processedByUser: User | null
  processedOn: string | null
  updatedAt: string | null
}

//--- Payroll Summary Interfaces ---
// A single row in the summary table (e.g., Basic, HRA)
export interface SummaryTableRow {
  componentName: string
  // Values for each month + the final total value
  // Index 0 = Month 1, Index 1 = Month 2, ..., Last Index = Total
  values: number[]
}

// The complete data needed to render one table (Past or Future)
export interface PayrollSummaryTableData {
  // e.g., ['Apr', 'May', 'Jun', ..., 'Mar', 'Total']
  header: string[]
  earningsRows: SummaryTableRow[]
  deductionsRows: SummaryTableRow[]
  tdsRows?: any[]
  lopRows?: any[]
  // e.g., Total Gross, Total Deductions, Net Salary
  totals: {
    gross: SummaryTableRow
    deductions: SummaryTableRow
    net: SummaryTableRow
  }
}

export interface PayrollSummary {
  past: PayrollSummaryTableData
  future: PayrollSummaryTableData
}

export interface HistoricalPayroll {
  // YYYY-MM format, e.g., "2024-04"
  month: string
  // The full breakdown as it was saved after the payroll run
  calculationBreakdown: PayrollCalculationBreakdown
  tdsAfterCess?: number
  lop?: number
}

export interface PayrollState {
  payrollBatches: IncomingPayrollBatch[] | null
  //--- Needed for runnig a payroll ---
  runningPayroll: IncomingRunningPayrollBatch | null
  previousSalaries: Record<string, EmployeeSalary | null> | null // Record key is employeeId
  arrearLopDays: Record<string, number> | null // Same here
  tdsCalculationBatch: Record<string, TdsCalculationResult> | null
  tdsCalculation: { result: TdsCalculationResult; employeeId: string } | null
  isTdsPreviewLoading: false
  //--- --- --- --- --- --- --- --- --- --- ---
  userPayrollData: IncomingPayroll | null
  latestUserPayrollDate: string | null //To be used in revising and updating salaries. It's latest "user-payroll" date
  PayrollSummary: PayrollSummary | null
  loading: boolean
  error: string | null
}
